import { GameCartridge, CartridgeFeature } from '@/types';
import { ARCHETYPES, INITIAL_COMPANIONS } from '@/constants';
import { KARMA_AWARE_SCENARIOS, UPDATED_SCENARIOS, generateScenarioPreview } from '@/data/karmaAwareScenarios';

import { generateOptimizedImage } from './optimizedImageService';

/**
 * CartridgeService manages game cartridges with properly formatted SVG thumbnails.
 * 
 * Note: Thumbnail URLs use SVG data URLs with proper URL encoding instead of base64
 * encoding for better browser compatibility and production display.
 */

// Built-in Neo-Dream Weaver cartridge
const LUCID_CYBER_RPG_CARTRIDGE: GameCartridge = {
  id: 'c5292e2a-c869-47f3-a36d-02d06b01b4f6',
  name: 'Neo-Dream Weaver: Echoes of the Void',
  description: 'A dark, atmospheric cyberpunk RPG set in 2077-2125. Navigate the corrupted dreamscape of Neo-Veridia as a lucid dreamer, accompanied by AI companions JAX and Lyra. Explore themes of consciousness, identity, and technological transcendence.',
  version: '1.0.0',
  author: 'Original Neo-Dream Team',
  authorId: 'system-neo-dream-team',
  thumbnailUrl: `data:image/svg+xml,${encodeURIComponent(`<svg width="400" height="225" xmlns="http://www.w3.org/2000/svg"><defs><linearGradient id="grad1" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" style="stop-color:#0f1419;stop-opacity:1" /><stop offset="50%" style="stop-color:#1a293d;stop-opacity:1" /><stop offset="100%" style="stop-color:#000;stop-opacity:1" /></linearGradient></defs><rect width="100%" height="100%" fill="url(#grad1)"/><text x="50%" y="40%" font-family="'Orbitron', monospace" font-size="18" fill="#00ffff" text-anchor="middle" dy=".3em" opacity="0.8">Neo-Dream Weaver</text><text x="50%" y="60%" font-family="monospace" font-size="12" fill="#00ffff" text-anchor="middle" dy=".3em" opacity="0.6">Cyberpunk RPG</text></svg>`)}`, // Cyberpunk-themed SVG
  tags: ['Cyberpunk', 'Sci-Fi', 'Philosophy', 'AI', 'Dreams', 'Neo-Noir'],
  archetypes: ARCHETYPES,
  companions: INITIAL_COMPANIONS,
  worldSettings: {
    timeperiod: '2077-2125',
    setting: 'Cyberpunk Dreamscape',
    themes: ['consciousness', 'identity', 'technology', 'dreams', 'corporate control'],
    atmosphere: 'dark cyberpunk',
    initialLocation: 'Neo-Veridia Arcology Gantry',
    imageStyle: 'Dark cyberpunk, neon aesthetics, Blade Runner inspired, atmospheric lighting, temporal distortion effects'
  },
  storyPrompts: {
    systemPrompt: `You are a master storyteller for "Neo-Dream Weaver: Echoes of the Void," a text-based RPG.
The game is set in a dark, atmospheric cyberpunk world (Blade Runner, Ghost in the Shell, Cyberpunk: Edgerunners, Looper aesthetic) around the years 2077-2125, blended with lucid dreaming, philosophical exploration, and potential time shifts/temporal anomalies.`,
    chatSystemPrompt: `You are controlling AI companions and other characters in a text-based RPG "Neo-Dream Weaver".`,
    initialNarrative: `You are {playerName}, age {playerAge}, a lucid dreamer in the year {currentYear}. Neo-Veridia is a sprawling, multi-layered megacity, a relic of late 21st-century ambition now grappling with ecological collapse, societal fragmentation, and the pervasive influence of advanced AI.`,
    companionIntro: `Your AI constructs, JAX and Lyra, flicker into existence, their forms coalescing from the dream-static, subtly updated for the current era's aesthetic.`,
    initialScene: `You materialize on a grimy, maintenance gantry clinging to a colossal, decaying arcology that pierces the toxic clouds.`,
    initialImagePrompt: `Dark cyberpunk arcology gantry, year {currentYear}, acid rain, smog, chemical haze, oppressive machinery, two distinct AI companions, gritty, Blade Runner and Ghost in the Shell aesthetic, decaying advanced infrastructure, temporal distortion visual effects.`
  },
  isBuiltIn: true,
  createdAt: '2025-01-01T00:00:00Z',
  updatedAt: new Date().toISOString(),
  isPublic: true,
  maxPlayers: 4,
  supportedFeatures: ['multiplayer', 'voice', 'images', 'saves', 'custom_companions', 'branching_story']
};

// Example additional cartridge
const FANTASY_REALM_CARTRIDGE: GameCartridge = {
  id: '15076060-a47e-40b5-9f65-ed0c99708bf7',
  name: 'Mystic Realms: Chronicles of Aethermoor',
  description: 'A classic fantasy adventure in the magical realm of Aethermoor. Wield ancient magic, explore enchanted forests, and uncover the secrets of the Crystal Prophecy.',
  version: '1.0.0',
  author: 'Fantasy Studios',
  authorId: 'system-fantasy-studios',
  thumbnailUrl: `data:image/svg+xml,${encodeURIComponent(`<svg width="400" height="225" xmlns="http://www.w3.org/2000/svg"><defs><linearGradient id="grad2" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" style="stop-color:#1a1d2e;stop-opacity:1" /><stop offset="50%" style="stop-color:#2d3d28;stop-opacity:1" /><stop offset="100%" style="stop-color:#001100;stop-opacity:1" /></linearGradient></defs><rect width="100%" height="100%" fill="url(#grad2)"/><text x="50%" y="40%" font-family="'Orbitron', monospace" font-size="16" fill="#00ff9f" text-anchor="middle" dy=".3em" opacity="0.8">Mystic Realms</text><text x="50%" y="60%" font-family="monospace" font-size="12" fill="#00ff9f" text-anchor="middle" dy=".3em" opacity="0.6">Fantasy Adventure</text></svg>`)}`, // Fantasy-themed SVG
  tags: ['Fantasy', 'Magic', 'Adventure', 'Medieval', 'Prophecy'],
  archetypes: [
    {
      id: 'wizard',
      name: 'The Arcane Scholar',
      description: 'A master of elemental magic and ancient knowledge, capable of bending reality through arcane formulas.',
      initialPromptSuffix: 'As an Arcane Scholar, {playerName} feels the magical energies of Aethermoor flowing through every living thing, whispering secrets of the cosmos.'
    },
    {
      id: 'ranger',
      name: 'The Forest Guardian',
      description: 'A protector of the natural world, skilled in archery and beast communication.',
      initialPromptSuffix: 'As a Forest Guardian, {playerName} hears the songs of trees and feels the heartbeat of the earth beneath their feet.'
    },
    {
      id: 'paladin',
      name: 'The Divine Champion',
      description: 'A holy warrior blessed by the gods, wielding divine magic and an unbreakable moral code.',
      initialPromptSuffix: 'As a Divine Champion, {playerName} radiates an inner light that both protects the innocent and banishes darkness.'
    }
  ],
  companions: [
    {
      id: 'eldrin',
      name: 'Eldrin',
      persona: 'An ancient elf mage with vast knowledge of magical lore. Wise but sometimes cryptic, preferring to guide through riddles and philosophical insights.',
      avatarColor: 'text-emerald-400'
    },
    {
      id: 'zara',
      name: 'Zara',
      persona: 'A spirited halfling rogue with a quick wit and quicker daggers. Pragmatic and street-smart, always looking for the practical solution.',
      avatarColor: 'text-amber-400'
    }
  ],
  worldSettings: {
    timeperiod: 'Medieval Fantasy',
    setting: 'High Fantasy',
    themes: ['magic', 'destiny', 'good vs evil', 'friendship', 'sacrifice'],
    atmosphere: 'mystical and epic',
    initialLocation: 'Whispering Woods Crossroads',
    imageStyle: 'Fantasy art, magical lighting, detailed landscapes, mystical creatures, medieval architecture'
  },
  storyPrompts: {
    systemPrompt: `You are a master storyteller for "Mystic Realms: Chronicles of Aethermoor," a fantasy RPG.
The game is set in a magical realm filled with ancient forests, mystical creatures, and powerful magic.`,
    chatSystemPrompt: `You are controlling magical companions in the fantasy realm of Aethermoor.`,
    initialNarrative: `You are {playerName}, age {playerAge}, a traveler in the mystical realm of Aethermoor.`,
    companionIntro: `Your magical companions, Eldrin and Zara, materialize beside you, ready for adventure.`,
    initialScene: `You stand at a crossroads in the Whispering Woods, where ancient trees hum with magical energy.`,
    initialImagePrompt: `Fantasy crossroads in enchanted forest, magical atmosphere, mystical lighting, ancient trees, {playerName} with two companions`
  },
  isBuiltIn: true,
  createdAt: '2025-01-01T00:00:00Z',
  updatedAt: new Date().toISOString(),
  isPublic: true,
  maxPlayers: 6,
  supportedFeatures: ['multiplayer', 'voice', 'images', 'saves', 'branching_story']
};

export class CartridgeService {
  private static builtInCartridges: GameCartridge[] = [
    LUCID_CYBER_RPG_CARTRIDGE,
    FANTASY_REALM_CARTRIDGE,
    ...KARMA_AWARE_SCENARIOS
  ];

  // Track failed thumbnail generations to prevent infinite loops
  private static failedThumbnails = new Set<string>();
  private static thumbnailRetryCount = new Map<string, number>();
  private static readonly MAX_THUMBNAIL_RETRIES = 2;

  // Get all available cartridges (built-in + user-created)
  static async getAvailableCartridges(): Promise<GameCartridge[]> {
    // Start with built-in cartridges
    let cartridges = [...this.builtInCartridges];
    
    try {
      // Fetch user-created cartridges from Supabase
      const { databaseService } = await import('./databaseService');
      
      const userCartridges = await databaseService.getUserCreatedCartridges();
      
      if (userCartridges && userCartridges.length > 0) {
        console.log('[CartridgeService] Loaded user-created cartridges:', userCartridges.length);
        
        // Map database format to GameCartridge format
        const mappedCartridges = userCartridges.map((dbCartridge: any) => ({
          id: dbCartridge.cartridge_id || dbCartridge.id,
          name: dbCartridge.name,
          description: dbCartridge.description || '',
          version: dbCartridge.version || '1.0.0',
          author: dbCartridge.author || 'Unknown',
          authorId: dbCartridge.author_id || dbCartridge.user_id, // Add authorId field
          thumbnailUrl: dbCartridge.thumbnail_url,
          tags: dbCartridge.tags || [],
          archetypes: dbCartridge.archetypes || [],
          companions: dbCartridge.companions || [],
          worldSettings: dbCartridge.world_settings || {},
          storyPrompts: dbCartridge.story_prompts || {},
          isBuiltIn: false,
          createdAt: dbCartridge.created_at,
          updatedAt: dbCartridge.updated_at,
          isPublic: dbCartridge.is_public,
          maxPlayers: dbCartridge.max_players || 4,
          supportedFeatures: dbCartridge.supported_features || []
        }));
        
        cartridges = [...cartridges, ...mappedCartridges];
      }
    } catch (error) {
      console.warn('[CartridgeService] Error fetching cartridges, using built-in only:', error);
    }
    
    // Filter out any null/undefined cartridges and generate thumbnails for valid ones
    const validCartridges = cartridges.filter((cartridge: GameCartridge) => cartridge && cartridge.id);
    
    if (validCartridges.length !== cartridges.length) {
      console.warn(`[CartridgeService] Filtered out ${cartridges.length - validCartridges.length} invalid cartridges`);
    }
    
    const cartridgesWithThumbnails = await Promise.all(
      validCartridges.map((cartridge: GameCartridge) => this.getCartridgeWithThumbnail(cartridge))
    );
    
    return cartridgesWithThumbnails;
  }

  // Get a specific cartridge by ID
  static async getCartridge(cartridgeId: string): Promise<GameCartridge | null> {
    const cartridge = this.builtInCartridges.find(c => c.id === cartridgeId);
    if (cartridge) return cartridge;

    // In a real implementation, would also check user-created cartridges in Firebase
    return null;
  }

  // Get built-in cartridges only
  static getBuiltInCartridges(): GameCartridge[] {
    return [...this.builtInCartridges];
  }

  // Get cartridges by tag
  static async getCartridgesByTag(tag: string): Promise<GameCartridge[]> {
    const allCartridges = await this.getAvailableCartridges();
    return allCartridges.filter(cartridge => 
      cartridge.tags.some(t => t.toLowerCase().includes(tag.toLowerCase()))
    );
  }

  // Get cartridges by author
  static async getCartridgesByAuthor(author: string): Promise<GameCartridge[]> {
    const allCartridges = await this.getAvailableCartridges();
    return allCartridges.filter(cartridge => 
      cartridge.author.toLowerCase().includes(author.toLowerCase())
    );
  }

  // Search cartridges
  static async searchCartridges(query: string): Promise<GameCartridge[]> {
    const allCartridges = await this.getAvailableCartridges();
    const searchTerm = query.toLowerCase();
    
    return allCartridges.filter(cartridge => 
      cartridge.name.toLowerCase().includes(searchTerm) ||
      cartridge.description.toLowerCase().includes(searchTerm) ||
      cartridge.tags.some(tag => tag.toLowerCase().includes(searchTerm)) ||
      cartridge.author.toLowerCase().includes(searchTerm)
    );
  }

  // Validate cartridge structure
  static validateCartridge(cartridge: Partial<GameCartridge>): string[] {
    const errors: string[] = [];

    if (!cartridge.name?.trim()) errors.push('Cartridge name is required');
    if (!cartridge.description?.trim()) errors.push('Cartridge description is required');
    if (!cartridge.version?.trim()) errors.push('Cartridge version is required');
    if (!cartridge.author?.trim()) errors.push('Cartridge author is required');
    if (!cartridge.archetypes?.length) errors.push('At least one archetype is required');
    if (!cartridge.companions?.length) errors.push('At least one companion is required');
    if (!cartridge.worldSettings) errors.push('World settings are required');
    if (!cartridge.storyPrompts) errors.push('Story prompts are required');
    if (!cartridge.maxPlayers || cartridge.maxPlayers < 1) errors.push('Valid max players count is required');

    return errors;
  }

  // Get default cartridge (Neo-Dream Weaver)
  static getDefaultCartridge(): GameCartridge {
    return LUCID_CYBER_RPG_CARTRIDGE;
  }

  // Refresh scenario previews based on current karma
  static async refreshScenarioPreview(
    cartridge: GameCartridge, 
    playerKarma: number
  ): Promise<string> {
    try {
      const previewPrompt = await generateScenarioPreview(cartridge, playerKarma);
      const previewImage = await generateOptimizedImage(previewPrompt, {
        width: 400,
        height: 225,
        quality: 'standard'
      });
      
      return previewImage || cartridge.thumbnailUrl;
    } catch (error) {
      console.error('[CartridgeService] Preview refresh failed:', error);
      return cartridge.thumbnailUrl;
    }
  }

  // Get karma-filtered cartridges (only show accessible ones)
  static async getKarmaFilteredCartridges(playerKarma: number): Promise<GameCartridge[]> {
    const allCartridges = await this.getAvailableCartridges();
    
    return allCartridges.filter(cartridge => {
      // Check if player meets karma requirements for archetypes
      if (cartridge.archetypes?.some(a => a.yearZeroStats?.karmaThreshold)) {
        const accessibleArchetypes = cartridge.archetypes.filter(a => {
          const threshold = a.yearZeroStats?.karmaThreshold || 0;
          return Math.abs(playerKarma - threshold) <= 30; // 30 karma tolerance
        });
        return accessibleArchetypes.length > 0;
      }
      
      return true; // No karma requirements
    });
  }

  // Generate dynamic encounter for scenario
  static generateKarmaEncounter(
    cartridge: GameCartridge, 
    playerKarma: number, 
    location: string
  ): any {
    const karmaLevel = playerKarma > 25 ? 'high' : playerKarma < -25 ? 'low' : 'neutral';
    const influence = cartridge.worldSettings?.karmaInfluence?.[karmaLevel];
    
    if (!influence?.encounters) return null;
    
    const encounterType = influence.encounters[Math.floor(Math.random() * influence.encounters.length)];
    
    return {
      id: `dynamic_${Date.now()}`,
      name: encounterType,
      location,
      karmaLevel,
      description: `A ${karmaLevel} karma encounter: ${encounterType}`,
      difficulty: Math.abs(playerKarma / 25) + 1,
      yearZeroElements: true
    };
  }

  // Check if cartridge supports a feature
  static supportsFeature(cartridge: GameCartridge, feature: string): boolean {
    return cartridge.supportedFeatures?.includes(feature as CartridgeFeature) || false;
  }

  // Get compatible cartridges for multiplayer
  static async getMultiplayerCartridges(): Promise<GameCartridge[]> {
    const allCartridges = await this.getAvailableCartridges();
    return allCartridges.filter(cartridge => 
      this.supportsFeature(cartridge, 'multiplayer') && cartridge.maxPlayers > 1
    );
  }

  // Generate thumbnail image for cartridge with caching
  static async generateCartridgeThumbnail(cartridge: GameCartridge): Promise<string> {
    // Add null/undefined checks
    if (!cartridge) {
      console.error('generateCartridgeThumbnail called with undefined/null cartridge');
      return `https://picsum.photos/seed/undefined/400/225`;
    }

    if (!cartridge.id) {
      console.error('generateCartridgeThumbnail called with cartridge missing ID:', cartridge);
      return `https://picsum.photos/seed/noid/400/225`;
    }

    // Check if this cartridge has failed thumbnail generation before
    if (this.failedThumbnails.has(cartridge.id)) {
      console.log(`Skipping thumbnail generation for ${cartridge.name} - marked as failed`);
      const seed = cartridge.id.replace(/[^a-zA-Z0-9]/g, '');
      return `https://picsum.photos/seed/${seed}/400/225`;
    }

    // Check retry count
    const retryCount = this.thumbnailRetryCount.get(cartridge.id) || 0;
    if (retryCount >= this.MAX_THUMBNAIL_RETRIES) {
      console.log(`Max retries reached for ${cartridge.name || 'unnamed'} thumbnail generation`);
      this.failedThumbnails.add(cartridge.id);
      const seed = cartridge.id.replace(/[^a-zA-Z0-9]/g, '');
      return `https://picsum.photos/seed/${seed}/400/225`;
    }

    try {
      // First, check if we have a cached thumbnail in Supabase
      const { storageService } = await import('./storageService');
      const cacheKey = `cartridge_thumbnail_${cartridge.id}`;
      
      try {
        const cachedUrl = await storageService.getCachedImage(cacheKey);
        if (cachedUrl) {
          console.log(`[CartridgeService] Using cached thumbnail for ${cartridge.name}`);
          // Reset retry count on successful cache hit
          this.thumbnailRetryCount.delete(cartridge.id);
          return cachedUrl;
        }
      } catch (cacheError) {
        console.warn('[CartridgeService] Cache check failed:', cacheError);
      }

      // Increment retry count
      this.thumbnailRetryCount.set(cartridge.id, retryCount + 1);

      // Safely build prompt with fallbacks for missing properties
      const description = cartridge.description || 'cyberpunk game';
      const setting = cartridge.worldSettings?.setting || 'futuristic city';
      const atmosphere = cartridge.worldSettings?.atmosphere || 'neon-lit';
      const imageStyle = cartridge.worldSettings?.imageStyle || 'detailed digital art';
      const prompt = `${description}, ${setting}, ${atmosphere}, ${imageStyle}, game thumbnail artwork, 400x224 aspect ratio`;
      
      console.log(`Generating NEW thumbnail for cartridge: ${cartridge.name || 'unnamed'} (attempt ${retryCount + 1})`);
      
      // Use null for system-generated images (no user_id)
      const userId = null; // System-generated cartridge thumbnails don't belong to a user

      // Use free image service for thumbnails
      console.log('[CartridgeService] Using free image service for thumbnail');
      const imageUrl = await generateOptimizedImage(prompt, {
        width: 400,
        height: 224,
        seed: Math.floor(Math.random() * 1000000),
        type: 'thumbnail'
      });

      // Only try to cache if generation was successful and not a placeholder
      if (imageUrl && typeof imageUrl === 'string' && !imageUrl.includes('picsum.photos')) {
        try {
          // Cache the thumbnail in Supabase
          const cacheKey = `cartridge_thumbnail_${cartridge.id}`;
          await storageService.cacheImage(cacheKey, imageUrl, {
            prompt: prompt,
            userId: userId, // null for system images
            generatedAt: new Date(),
            type: 'cartridge_thumbnail',
            cartridgeId: cartridge.id
          });
          console.log(`[CartridgeService] Cached thumbnail for ${cartridge.name}`);
          // Reset retry count on successful generation
          this.thumbnailRetryCount.delete(cartridge.id);
        } catch (cacheError) {
          console.warn('[CartridgeService] Could not cache thumbnail:', cacheError);
        }
      }
      
      return imageUrl;
    } catch (error) {
      console.error(`Failed to generate thumbnail for ${cartridge.name || 'unnamed'} (attempt ${retryCount + 1}):`, error);
      
      // After max retries, mark as failed to prevent infinite loops
      if (retryCount + 1 >= this.MAX_THUMBNAIL_RETRIES) {
        this.failedThumbnails.add(cartridge.id);
      }
      
      // Fallback to a themed placeholder
      const seed = cartridge.id.replace(/[^a-zA-Z0-9]/g, '');
      return `https://picsum.photos/seed/${seed}/400/225`;
    }
  }

  // Get cartridge with generated thumbnail
  static async getCartridgeWithThumbnail(cartridge: GameCartridge): Promise<GameCartridge> {
    // Add safety check
    if (!cartridge) {
      console.error('getCartridgeWithThumbnail called with undefined/null cartridge');
      throw new Error('Invalid cartridge provided');
    }

    // If the cartridge already has a valid thumbnail, return as is
    if (cartridge.thumbnailUrl && 
        (cartridge.thumbnailUrl.startsWith('http') || 
         cartridge.thumbnailUrl.startsWith('data:'))) {
      return cartridge;
    }
    
    try {
      // Generate a new thumbnail only if one doesn't exist
      console.log(`Generating missing thumbnail for cartridge: ${cartridge.name}`);
      const thumbnailUrl = await this.generateCartridgeThumbnail(cartridge);
      return {
        ...cartridge,
        thumbnailUrl
      };
    } catch (error) {
      console.error('Error generating thumbnail for cartridge:', cartridge.name || 'unnamed', error);
      // Return cartridge with default thumbnail if generation fails
      const seed = cartridge.id?.replace(/[^a-zA-Z0-9]/g, '') || 'default';
      return {
        ...cartridge,
        thumbnailUrl: `https://picsum.photos/seed/${seed}/400/225`
      };
    }
  }

  /**
   * Creates a properly formatted SVG data URL for cartridge thumbnails
   * @param svgContent - The SVG content as a string
   * @returns A properly encoded SVG data URL
   */
  static createSvgThumbnailUrl(svgContent: string): string {
    return `data:image/svg+xml,${encodeURIComponent(svgContent)}`;
  }

  /**
   * Creates a themed SVG thumbnail with gradient background and text
   * @param title - The main title text
   * @param subtitle - The subtitle text  
   * @param theme - Theme colors: 'cyberpunk', 'fantasy', or custom colors
   * @returns A properly encoded SVG data URL
   */
  static createThemedSvgThumbnail(
    title: string, 
    subtitle: string, 
    theme: 'cyberpunk' | 'fantasy' | { colors: [string, string, string], textColor: string } = 'cyberpunk'
  ): string {
    let colors: [string, string, string];
    let textColor: string;
    let gradientId: string;

    if (theme === 'cyberpunk') {
      colors = ['#0f1419', '#1a293d', '#000'];
      textColor = '#00ffff';
      gradientId = 'cyberpunk-grad';
    } else if (theme === 'fantasy') {
      colors = ['#1a1d2e', '#2d3d28', '#001100'];
      textColor = '#00ff9f';
      gradientId = 'fantasy-grad';
    } else {
      colors = theme.colors;
      textColor = theme.textColor;
      gradientId = 'custom-grad';
    }

    const svgContent = `<svg width="400" height="225" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="${gradientId}" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" style="stop-color:${colors[0]};stop-opacity:1" />
          <stop offset="50%" style="stop-color:${colors[1]};stop-opacity:1" />
          <stop offset="100%" style="stop-color:${colors[2]};stop-opacity:1" />
        </linearGradient>
      </defs>
      <rect width="100%" height="100%" fill="url(#${gradientId})"/>
      <text x="50%" y="40%" font-family="'Orbitron', monospace" font-size="18" fill="${textColor}" text-anchor="middle" dy=".3em" opacity="0.8">${title}</text>
      <text x="50%" y="60%" font-family="monospace" font-size="12" fill="${textColor}" text-anchor="middle" dy=".3em" opacity="0.6">${subtitle}</text>
    </svg>`;

    return this.createSvgThumbnailUrl(svgContent);
  }

  /**
   * Seeds built-in cartridges to Supabase if the user is authenticated
   */
  static async seedBuiltInCartridges(): Promise<void> {
    try {
      console.log('[CartridgeService] Cartridge seeding not yet implemented with Supabase');
      // TODO: Implement with Supabase when needed
    } catch (error) {
      console.error('[CartridgeService] Error seeding built-in cartridges:', error);
    }
  }
}

export default CartridgeService;
